import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import DebounceInput from "react-debounce-input";
import { useSelector } from "react-redux";

import { FaSwatchbook, FaEdit } from "react-icons/fa";
import { GiBookCover, GiBookmarklet } from "react-icons/gi";
import { AiOutlineClockCircle } from "react-icons/ai";
import { HiOutlineMagnifyingGlass, HiOutlineAcademicCap, HiOutlinePlus, HiOutlineSparkles } from "react-icons/hi2";

import Rating from "./rating";
import Loader from "../../../../components/loader";

import api from "../../../../services/api";

export default function List({ onWorkshopClick }) {
  const [workshops, setWorkshops] = useState();
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ search: "", perPage: 100, page: 1, short: true, type: undefined, sort: "rating" });
  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    fetchWorkshops();
  }, [filter.search, filter.type]);

  const fetchWorkshops = async () => {
    try {
      setLoading(true);
      const { data, ok } = await api.post("/workshop/search", filter);
      if (!ok) return toast.error("Error fetching workshops");
      setWorkshops(data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  if (!workshops) return <Loader color="black" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto p-6">
        <div className="flex items-center justify-between mb-8 gap-4 max-w-screen-2xl mx-auto">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Workshops & Tutorials</h1>
            <p className="text-gray-600">Discover and explore {workshops?.length || 0} curated workshops, tutorials, and learning materials</p>
          </div>
          <div className="flex items-center justify-end gap-3">
            <div className="relative">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <DebounceInput
                debounceTimeout={300}
                className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 w-64"
                placeholder="Search workshops..."
                value={filter.search}
                onChange={(e) => {
                  e.persist();
                  setFilter((prev) => ({ ...prev, search: e.target.value }));
                }}
              />
            </div>
            {["admin", "super_admin"].includes(user?.role) && (
              <Link
                to="/selecole/editor"
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200">
                <HiOutlinePlus className="w-4 h-4" />
                New Workshop
              </Link>
            )}
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8 max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-xl flex items-center justify-center">
              <HiOutlineSparkles className="w-4 h-4 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Filter by Category</h3>
          </div>
          <div className="flex items-center gap-3">
            <SelectCategory selectedType={filter.type} onChange={(type) => setFilter((prev) => ({ ...prev, type }))} />
            {loading && <Loader size="small" color="black" />}
          </div>
        </div>

        {workshops.length === 0 ? (
          <div className="text-center py-16 max-w-screen-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
              <HiOutlineAcademicCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No workshops found</h3>
              <p className="text-gray-500 mb-6">{filter.search ? `No workshops match "${filter.search}"` : "Get started by creating your first workshop"}</p>
              {["admin", "super_admin"].includes(user?.role) && !filter.search && (
                <Link
                  to="/selecole/editor"
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors duration-200">
                  <HiOutlinePlus className="w-4 h-4" />
                  Create Workshop
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 max-w-screen-2xl mx-auto">
            {workshops.map((workshop) => (
              <Card key={workshop._id} workshop={workshop} onWorkshopClick={onWorkshopClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Card = ({ workshop, onWorkshopClick }) => {
  const user = useSelector((state) => state.Auth.user);

  return (
    <div className="group bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-gray-300 transition-all duration-300 overflow-hidden flex flex-col h-full transform hover:-translate-y-1 relative">
      {["admin", "super_admin"].includes(user?.role) && (
        <Link
          to={`/selecole/editor/${workshop._id}`}
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-600 hover:text-gray-900 px-2 py-1 rounded-lg text-xs font-medium shadow-sm border border-gray-200 transition-all duration-200"
          onClick={(e) => e.stopPropagation()}>
          <FaEdit className="w-3 h-3" />
          Edit
        </Link>
      )}

      <div className="relative w-full h-[200px] overflow-hidden">
        <img
          src={
            workshop.image ||
            "https://bank.cellar-c2.services.clever-cloud.com/file/assets/96de0808388005150c42fd8b3369d498/ChatGPT%20Image%20Apr%205%2C%202025%2C%2003_49_44%20PM.png"
          }
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          alt={workshop.name}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {workshop.type && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">
              {getTypeIcon(workshop.type)}
              <span className="capitalize">{workshop.type}</span>
            </span>
          </div>
        )}
        {workshop.read_time && (
          <div className="absolute bottom-3 right-3">
            <div className="inline-flex items-center gap-1 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-medium">
              <AiOutlineClockCircle className="w-3 h-3" />
              {workshop.read_time}
            </div>
          </div>
        )}
      </div>

      {onWorkshopClick ? (
        <div onClick={() => onWorkshopClick(workshop)} className="cursor-pointer flex-1 p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700">Workshop</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-700 transition-colors duration-200 line-clamp-2 mb-2">{workshop.name}</h3>
          <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">{workshop.description}</p>
        </div>
      ) : (
        <Link to={`/selecole/learn/${workshop._id}`} className="flex-1 p-6 flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-700 transition-colors duration-200 line-clamp-2 mb-2">{workshop.name}</h3>
          <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4 flex-1">{workshop.description}</p>

          {workshop.rating_score && workshop.rating_count ? (
            <div className="mt-auto pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm">
                <Rating value={workshop.rating_score} />
                <span className="font-medium text-gray-700">{workshop.rating_score}</span>
                <span className="text-gray-500">({workshop.rating_count})</span>
              </div>
            </div>
          ) : (
            <div className="mt-auto pt-4 border-t border-gray-100">
              <div className="h-2"></div>
            </div>
          )}
        </Link>
      )}
    </div>
  );
};

function getTypeIcon(type) {
  switch (type) {
    case "book":
      return <GiBookCover />;
    case "mooc":
      return <FaSwatchbook />;
    case "tutorial":
      return <GiBookmarklet />;
    default:
      return <GiBookmarklet />;
  }
}

const SelectCategory = ({ selectedType, onChange }) => {
  const [categories, setCategories] = useState(["book", "mooc", "tutorial"]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, ok } = await api.post("/workshop/categories");
      if (ok && data && data.length) {
        setCategories(data);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {loading ? (
        <span className="text-sm text-gray-500">Loading categories...</span>
      ) : (
        <>
          <button
            onClick={() => onChange(undefined)}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
              selectedType === undefined ? "bg-gray-900 text-white shadow-sm" : "text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
            }`}>
            All Categories
          </button>

          {categories.map((category) => (
            <button
              key={category}
              onClick={() => onChange(category)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                selectedType === category ? "bg-purple-600 text-white shadow-sm" : "text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              }`}>
              <span className={selectedType === category ? "text-white" : "text-gray-600"}>{getTypeIcon(category)}</span>
              <span className="capitalize">{category}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
};
